import { useContext } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import logo from "../../assets/images/RedP_LogoT.png";
import "./Downloads.css";

const BIG3_VERSION = "1.0.0";

const BIG3_MACOS_URL = "https://github.com/calebcoomer/waterview-app-updates/releases/download/MacOS_v1.0.8/Big3-Agent-1.0.8-macOS-arm64.dmg";

const BIG3_WINDOWS_URL = "https://github.com/calebcoomer/waterview-app-updates/releases/download/windows/Big3-Agent-1.0.0-Windows-x64-portable.zip";

// Drop a recording in public/videos/ and fill in the matching entry below.
// While an entry is null its accordion shows a short note instead of a player.
const SETUP_VIDEOS = {
  macos: { src: "/videos/howtoinstall.mp4", width: 1268, height: 1106 },
  windows: { src: "/videos/howtoinstall-windows.mp4", width: 1280, height: 1024 },
};

const SourceIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 21h18" />
    <path d="M5 21V8l7-4 7 4v13" />
    <path d="M9 21v-6h6v6" />
  </svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.37 1.43c0 1.14-.42 2.2-1.25 3.06-.99 1.03-2.13 1.63-3.27 1.54-.13-1.1.42-2.27 1.18-3.05.87-.9 2.29-1.5 3.34-1.55zM20.9 17.06c-.55 1.27-.82 1.84-1.53 2.96-.99 1.56-2.38 3.5-4.1 3.51-1.53.02-1.92-.98-4-.97-2.08.01-2.51.99-4.04.97-1.72-.01-3.04-1.76-4.03-3.32-2.78-4.38-3.25-9.58-1.54-12.19C3.88 6.16 5.8 5.07 7.61 5.07c1.84 0 3 1.01 4.52 1.01 1.48 0 2.38-1.01 4.51-1.01 1.61 0 3.32.88 4.54 2.4-3.99 2.19-3.34 7.89.72 9.59z" />
  </svg>
);

const WindowsIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M2.6 5.36 10.3 4.3v7.19H2.6zM11.42 4.17 21.4 2.8v8.69h-9.98zM2.6 12.51h7.7v7.19L2.6 18.64zM11.42 12.51H21.4v8.69l-9.98-1.37z" />
  </svg>
);

const ChevronIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const SetupVideo = ({ label, video }) => (
  <details className="setup-video">
    <summary>
      <span className="setup-video-summary">
        <span>Need help?</span>
        <strong>{label}</strong>
      </span>
      <span className="setup-video-toggle" aria-hidden="true">
        <ChevronIcon />
      </span>
    </summary>
    <div className="setup-video-content">
      {video ? (
        <video
          controls
          playsInline
          preload="metadata"
          width={video.width}
          height={video.height}
          style={{ aspectRatio: `${video.width} / ${video.height}` }}
          aria-label={label}
        >
          <source src={video.src} type="video/mp4" />
          Your browser does not support embedded video.
        </video>
      ) : (
        <p className="setup-video-pending">
          This walkthrough is being recorded and will appear here soon. The
          written steps above cover the full process.
        </p>
      )}
    </div>
  </details>
);

const Downloads = () => {
  const { user } = useContext(AuthContext);
  const redpPath = user?.id ? `/redp/${user.id}` : "/";
  const downloadBig3 = (url) => window.location.assign(url);

  return (
    <main className="downloads-page">
    <header className="downloads-header">
      <Link to={redpPath} className="downloads-brand" aria-label={user ? "Back to RedP" : "Go to RedP sign in"}>
        <img src={logo} alt="RedP" />
      </Link>
      <Link to={redpPath} className="downloads-nav-link">
        {user ? "← Back to RedP" : "← Go to sign in"}
      </Link>
    </header>

    <section className="downloads-hero">
      <span className="downloads-eyebrow">RedP Tools</span>
      <h1>Less searching. More time for the work that matters.</h1>
      <p>Purpose-built tools that streamline repetitive real estate research and document collection.</p>
    </section>

    <section className="tool-release-card" aria-labelledby="big-3-title">
      <div className="tool-release-main">
        <h2 id="big-3-title">Big 3</h2>
        <p className="tool-description">
          Automatically collects property documents from the Property Appraiser,
          Tax Collector, and Clerk of Courts.
        </p>

        <div className="source-grid">
          {[
            ["Property Appraiser", "Property records in one streamlined workflow."],
            ["Tax Collector", "Tax information without repetitive searching."],
            ["Clerk of Courts", "Court records gathered alongside property data."],
          ].map(([title, description]) => (
            <article className="source-card" key={title}>
              <SourceIcon />
              <div><h3>{title}</h3><p>{description}</p></div>
            </article>
          ))}
        </div>
      </div>

      <aside className="release-panel">
        <span>Latest release</span>
        <h3>Big 3 is ready.</h3>
        <p>Big 3 Agent {BIG3_VERSION}. Choose your platform.</p>

        <div className="release-download-group">
          <button
            className="release-download-button"
            type="button"
            onClick={() => downloadBig3(BIG3_MACOS_URL)}
          >
            <AppleIcon />
            <span className="release-download-label">
              Download for macOS
              <small>Apple silicon</small>
            </span>
          </button>

          <button
            className="release-download-button"
            type="button"
            onClick={() => downloadBig3(BIG3_WINDOWS_URL)}
            disabled={!BIG3_WINDOWS_URL}
          >
            <WindowsIcon />
            <span className="release-download-label">
              Download for Windows
              <small>Windows 10 &amp; 11 · 64-bit · portable ZIP</small>
            </span>
          </button>
        </div>

        {!BIG3_WINDOWS_URL && (
          <p className="release-coming-soon">The Windows build is coming soon.</p>
        )}
      </aside>
    </section>

    <section className="setup-guide" aria-labelledby="setup-guide-title">
      <div className="setup-guide-intro">
        <span className="setup-guide-eyebrow">First-time setup</span>
        <h2 id="setup-guide-title">Install once. Start searching in minutes.</h2>
        <p>
          Big 3 is not code signed on either platform. macOS will block the first
          launch, and Windows will show a SmartScreen warning, so you will need to
          approve the app once before it runs. You only have to do this the first
          time.
        </p>
      </div>

      <div className="setup-videos">
        <SetupVideo label="Watch the macOS first-time setup video" video={SETUP_VIDEOS.macos} />
        <SetupVideo label="Watch the Windows first-time setup video" video={SETUP_VIDEOS.windows} />
      </div>

      <div className="setup-guide-grid">
        <article className="setup-guide-card">
          <h3>Install and approve Big 3 on macOS</h3>
          <ol className="setup-steps">
            <li>
              <span aria-hidden="true">1</span>
              <div><strong>Install the app</strong><p>Open the DMG and drag Big 3 Agent into Applications.</p></div>
            </li>
            <li>
              <span aria-hidden="true">2</span>
              <div><strong>Try to open it once</strong><p>Double-click Big 3 Agent, then dismiss the macOS security warning.</p></div>
            </li>
            <li>
              <span aria-hidden="true">3</span>
              <div><strong>Allow the app</strong><p>Open System Settings → Privacy &amp; Security, scroll to Security, and click Open Anyway.</p></div>
            </li>
            <li>
              <span aria-hidden="true">4</span>
              <div><strong>Confirm the launch</strong><p>Authenticate with your Mac password or Touch ID, then click Open.</p></div>
            </li>
          </ol>
          <p className="setup-note">
            Open Anyway appears only after the first launch attempt and is
            available for about one hour. Only approve a copy downloaded from
            this page.
          </p>
        </article>

        <article className="setup-guide-card">
          <h3>Unzip and run Big 3 on Windows</h3>
          <ol className="setup-steps">
            <li>
              <span aria-hidden="true">1</span>
              <div><strong>Unzip the download</strong><p>Right-click the ZIP in your Downloads folder and choose Extract All.</p></div>
            </li>
            <li>
              <span aria-hidden="true">2</span>
              <div><strong>Open the app</strong><p>Double-click the Big 3 Agent application inside the extracted folder.</p></div>
            </li>
            <li>
              <span aria-hidden="true">3</span>
              <div><strong>Get past the warning</strong><p>On “Windows protected your PC”, click More info, then Run anyway.</p></div>
            </li>
            <li>
              <span aria-hidden="true">4</span>
              <div><strong>Keep the folder</strong><p>Big 3 runs from wherever you put it, so move the folder somewhere permanent, then sign in.</p></div>
            </li>
          </ol>
          <p className="setup-note">
            Big 3 is portable — there is no installer and nothing is added
            to Program Files. SmartScreen warns because the app is not yet
            signed. Only click Run anyway for a copy downloaded from this page.
          </p>
        </article>

        <article className="setup-guide-card setup-guide-card-wide">
          <h3>Run your first property search</h3>
          <ol className="setup-steps">
            <li>
              <span aria-hidden="true">1</span>
              <div><strong>Sign in</strong><p>Use the same account you use for RedP.</p></div>
            </li>
            <li>
              <span aria-hidden="true">2</span>
              <div><strong>Choose a county</strong><p>Select a supported county and enter an owner, address, parcel, or other available search value.</p></div>
            </li>
            <li>
              <span aria-hidden="true">3</span>
              <div><strong>Start the search</strong><p>Choose Run for the full workflow or Quick run for a reduced search.</p></div>
            </li>
            <li>
              <span aria-hidden="true">4</span>
              <div><strong>Open your results</strong><p>Track progress in the queue and find collected files in Documents → Big3 Agent Output.</p></div>
            </li>
          </ol>
        </article>
      </div>

      <a
        className="setup-help-link"
        href="https://support.apple.com/guide/mac-help/open-a-mac-app-from-an-unknown-developer-mh40616/mac"
        target="_blank"
        rel="noreferrer"
      >
        View Apple&apos;s macOS security instructions ↗
      </a>
    </section>
    </main>
  );
};

export default Downloads;
